import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Badge, Box, Button, CircularProgress, IconButton, InputAdornment, MenuItem, Paper, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import NotaFormDialog from "../components/NotaFormDialog";
import NotaArchivosDialog from "../components/NotaArchivosDialog";
import { getCatalogosNotas, listarNotas, mensajeError } from "../services/notasService";

const cellSx = { py: 0.5, px: 1, fontSize: 12.5 };
const headSx = { fontWeight: 800, whiteSpace: "nowrap" };
const OPCIONES_FILAS = [10, 25, 50, 100];

// Notas: cartas / comunicaciones enviadas por cada área. Búsqueda y paginación en el servidor; cada nota puede llevar archivos adjuntos.
export default function NotasPage() {
  const [catalogos, setCatalogos] = useState({ sucursales: [], areas: [] });
  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState({ texto: "", severidad: "success" });

  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");
  const [sucursal, setSucursal] = useState("");
  const [area, setArea] = useState("");
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(25);

  const [formAbierto, setFormAbierto] = useState(false);
  const [notaEditar, setNotaEditar] = useState(null);
  const [notaArchivos, setNotaArchivos] = useState(null);

  useEffect(() => {
    getCatalogosNotas().then(({ data }) => setCatalogos(data)).catch((e) => setError(mensajeError(e, "No se pudieron cargar los catálogos.")));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setQ(buscar.trim()); setPagina(0); }, 300);
    return () => clearTimeout(t);
  }, [buscar]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await listarNotas({ q: q || undefined, sucursal: sucursal || undefined, area: area || undefined, page: pagina + 1, limit: limite });
      setFilas(data.rows);
      setTotal(data.total);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar las notas."));
    } finally {
      setCargando(false);
    }
  }, [q, sucursal, area, pagina, limite]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNueva = () => { setNotaEditar(null); setFormAbierto(true); };
  const abrirEditar = (n) => { setNotaEditar(n); setFormAbierto(true); };

  const alGuardar = ({ id, nueva, aviso: problema }) => {
    setFormAbierto(false);
    setAviso(problema ? { texto: problema, severidad: "warning" } : { texto: nueva ? `Nota ${id} registrada.` : `Nota ${id} actualizada.`, severidad: "success" });
    if (nueva) { setBuscar(""); setQ(""); setPagina(0); }
    cargar();
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Notas"
        subtitle="Cartas y comunicaciones enviadas por cada área, con sus archivos"
        actions={<Button variant="contained" size="small" startIcon={<AddIcon />} onClick={abrirNueva} disabled={!catalogos.areas.length}>Agregar nota</Button>}
      />

      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
          <TextField
            size="small" placeholder="Buscar por código, destinatario, referencia…" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ width: { xs: "100%", sm: 340 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <TextField size="small" select label="Sucursal" value={sucursal} onChange={(e) => { setSucursal(e.target.value); setPagina(0); }} sx={{ width: 150 }}>
            <MenuItem value="">Todas</MenuItem>
            {catalogos.sucursales.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Área" value={area} onChange={(e) => { setArea(e.target.value); setPagina(0); }} sx={{ width: 180 }}>
            <MenuItem value="">Todas</MenuItem>
            {catalogos.areas.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          {cargando && <CircularProgress size={18} />}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>{error}</Alert>}

        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: "68vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Código", "Fecha", "Sucursal", "Área", "Destinatario", "Referencia", "Enviado por"].map((h) => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
                  <TableCell sx={headSx} align="center">Archivos</TableCell>
                  <TableCell sx={headSx} align="center">Modificar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!filas.length ? (
                  <TableRow><TableCell colSpan={9} sx={cellSx}><Typography variant="body2" color="text.secondary">{cargando ? "Cargando…" : "No hay notas para mostrar."}</Typography></TableCell></TableRow>
                ) : filas.map((n) => (
                  <TableRow key={n.id} hover>
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{n.id}</TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{n.fecha}</TableCell>
                    <TableCell sx={cellSx}>{n.sucursal}</TableCell>
                    <TableCell sx={cellSx}>{n.area}</TableCell>
                    <TableCell sx={cellSx}>{n.destinatario}</TableCell>
                    <TableCell sx={cellSx}>{n.referencia}</TableCell>
                    <TableCell sx={cellSx}>{n.enviado_por}</TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Tooltip title="Ver y subir archivos">
                        <IconButton size="small" onClick={() => setNotaArchivos(n)}>
                          <Badge badgeContent={n.total_archivos} color="primary" max={99}><AttachFileIcon fontSize="small" /></Badge>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Tooltip title="Modificar nota"><IconButton size="small" onClick={() => abrirEditar(n)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={total} page={pagina} rowsPerPage={limite} rowsPerPageOptions={OPCIONES_FILAS}
            onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
            labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      <NotaFormDialog open={formAbierto} nota={notaEditar} catalogos={catalogos} onClose={() => setFormAbierto(false)} onGuardado={alGuardar} />
      <NotaArchivosDialog nota={notaArchivos} onClose={() => setNotaArchivos(null)} onCambio={cargar} />

      <Snackbar open={!!aviso.texto} autoHideDuration={4500} onClose={() => setAviso((a) => ({ ...a, texto: "" }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={aviso.severidad} variant="filled" onClose={() => setAviso((a) => ({ ...a, texto: "" }))}>{aviso.texto}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
